import React, { useState,useRef } from 'react';
import { ArrowLeft, Server, AlertTriangle, HardDrive, Shield } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const EC2Page = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { ec2Data, region, accountName, duration } = location.state || {};

  
  const CustomTooltip = ({ text, position = 'left' }) => {
    const [showTooltip, setShowTooltip] = useState(false);
  
    return (
      <div className="relative inline-block">
        <div
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-pointer text-xs text-red-600 hover:text-red-800"
        >
          Error details
        </div>
        {showTooltip && (
          <div className={`absolute z-50 w-96 p-3 text-sm bg-gray-900 text-white rounded shadow-lg -mt-1 ${position === 'right' ? 'right-0' : 'left-0'}`}>
            <div className="whitespace-normal break-words">{text}</div>
          </div>
        )}
      </div>
    );
  };

  
    const LongTextCell = ({ text, maxLength = 30 }) => {
      const [showTooltip, setShowTooltip] = useState(false);
      const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
      const tooltipRef = useRef(null);
      const cellRef = useRef(null);
      
      if (!text || text.length <= maxLength) {
        return <span>{text}</span>;
      }
      
      const handleMouseEnter = (e) => {
        setShowTooltip(true);
        setTimeout(() => {
          if (cellRef.current && tooltipRef.current) {
            const cellRect = cellRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const spaceBelow = viewportHeight - cellRect.bottom;
            const tooltipHeight = tooltipRect.height;
            
            if (spaceBelow < tooltipHeight) {
              setTooltipPosition({
                top: -tooltipHeight - 5,
                left: 0
              });
            } else {
              setTooltipPosition({
                top: "100%",
                left: 0
              });
            }
          }
        }, 0);
      };
    
      return (
        <div className="relative inline-block">
          <div
            ref={cellRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setShowTooltip(false)}
            className="cursor-pointer border-b border-dotted border-gray-400"
          >
            {text.substring(0, maxLength)}...
          </div>
          {showTooltip && (
            <div 
              ref={tooltipRef}
              className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
              style={{
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                maxWidth: "250px",
                wordBreak: "break-word"
              }}
            >
              {text}
            </div>
          )}
        </div>
      );
    };
  
  const ErrorStatus = ({ errorCode, errorMessage }) => {
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
          errorCode ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {errorCode ? 'Error' : 'Success'}
        </span>
        {errorMessage && (
          <CustomTooltip text={errorMessage} position="right" />
        )}
      </div>
    );
  };
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">EC2 Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Configuration Changes</div>
            <div className="text-xl font-semibold text-[#003366]">
              {ec2Data?.summary?.configurationCount || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Errors</div>
            <div className="text-xl font-semibold text-[#003366]">
              {ec2Data?.summary?.errorCount || 0}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
  <div className="bg-white rounded-lg shadow-lg p-6">
    
    <div className="flex items-center gap-3 mb-4">
      <Server className="h-6 w-6 text-[#003366]" />
      <h3 className="text-lg font-semibold text-[#003366]">Instance Operations</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Created</span>
        <span className="font-semibold">{ec2Data?.summary?.instanceOperations?.runCount || 0}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Started</span>
        <span className="font-semibold">{ec2Data?.summary?.instanceOperations?.startCount || 0}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Stopped</span>
        <span className="font-semibold">{ec2Data?.summary?.instanceOperations?.stopCount || 0}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-gray-600">Terminated</span>
        <span className="font-semibold">{ec2Data?.summary?.instanceOperations?.terminateCount || 0}</span>
      </div>
    </div>
  </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <HardDrive className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Volume Operations</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Created</span>
              <span className="font-semibold">{ec2Data?.summary?.volumeOperations?.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Deleted</span>
              <span className="font-semibold">{ec2Data?.summary?.volumeOperations?.deleteCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Attached/Detached</span>
              <span className="font-semibold">
                {(ec2Data?.summary?.volumeOperations?.attachCount || 0) + '/' + 
                 (ec2Data?.summary?.volumeOperations?.detachCount || 0)}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Security & Images</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Security Group Changes</span>
              <span className="font-semibold">{ec2Data?.summary?.securityGroupCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Images Created</span>
              <span className="font-semibold">{ec2Data?.summary?.imageOperations?.createCount || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Images Deleted</span>
              <span className="font-semibold">{ec2Data?.summary?.imageOperations?.deleteCount || 0}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-[#003366] mb-4">EC2 Events</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                    Event Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                    Event Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Affected Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Event Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    Source IP
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-56">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ec2Data?.data && ec2Data.data.length > 0 ? (
                  ec2Data.data.map((event, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.username} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(event.eventtime).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.eventname} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <LongTextCell text={event.affected_resource} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {event.eventtype}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {event.sourceipaddress}
                      </td>
                      <td className="px-6 py-4">
                        <ErrorStatus 
                          errorCode={event.errorcode}
                          errorMessage={event.errormessage}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-lg font-medium">No data to display</p>
                        <p className="text-sm">No EC2 events were found for the selected time period and filters.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
    </div>
  );
};

export default EC2Page;